import { gql } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import classnames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  TaskStatusIcon,
  TaskStatusIconTaskFragment,
} from 'src/app/graph/checksPage/taskStatusIcon/TaskStatusIcon';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { ConditionalLink } from 'src/components/conditionalLink/ConditionalLink';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  LintDiagnosticLevel,
  ViolationLevel,
} from 'src/lib/graphqlTypes/types';

import {
  getLinkToTask,
  getLinkToTaskWorkflowFragment,
  getLinkToTaskWorkflowTaskFragment,
} from '../../../helpers/getLinkToTask';

interface Props {
  className?: string;
  task: GraphQLTypes.CheckWorkflowTaskItemFragment;
  crossedThreshold: boolean;
  size?: 'small' | 'base';
}

export const CheckWorkflowTaskItemFragment = gql`
  fragment CheckWorkflowTaskItemFragment on CheckWorkflowTask {
    id
    status
    createdAt
    completedAt
    ...getLinkToTaskWorkflowTaskFragment
    ...TaskStatusIconTaskFragment
    workflow {
      id
      baseVariant {
        id
        name
      }
      ...getLinkToTaskWorkflowFragment
    }
    ... on CompositionCheckTask {
      id
      result {
        graphCompositionID
        errors {
          message
          code
          locations {
            line
            column
          }
        }
      }
      buildResult {
        id
      }
    }
    ... on OperationsCheckTask {
      id
      result {
        id
        checkSeverity
        numberOfAffectedOperations
        crossedOperationThreshold
      }
    }
    ... on DownstreamCheckTask {
      results {
        failsUpstreamWorkflow
      }
    }
    ... on LintCheckTask {
      result {
        diagnostics {
          level
        }
        stats {
          ignoredCount
        }
      }
    }
    ... on FilterCheckTask {
      id
      status
      buildResult {
        id
        ... on FilterBuildCheckFailed {
          errors {
            message
            failedStep
          }
        }
      }
    }
    ... on ProposalsCheckTask {
      id
      status
      severityLevel
      proposalCoverage
      didOverrideProposalsCheckTask
    }
    ... on CustomCheckTask {
      id
      status
      result {
        violations {
          level
        }
      }
    }
  }
  ${getLinkToTaskWorkflowFragment}
  ${getLinkToTaskWorkflowTaskFragment}
  ${TaskStatusIconTaskFragment}
`;

export const CheckWorkflowTaskItem = ({
  task,
  crossedThreshold,
  className,
}: Props) => {
  const isBuild =
    task.__typename === 'CompositionCheckTask' ||
    task.__typename === 'FilterCheckTask';
  const isDownstream = task.__typename === 'DownstreamCheckTask';
  const isLinter = task.__typename === 'LintCheckTask';
  const isProposalCheck = task.__typename === 'ProposalsCheckTask';
  const isCustomCheck = task.__typename === 'CustomCheckTask';

  const customCheckErrorsCount = isCustomCheck
    ? (task.result?.violations.filter(
        (violation) => violation?.level === ViolationLevel.ERROR,
      ).length ?? 0)
    : 0;
  const customCheckWarningsCount = isCustomCheck
    ? (task.result?.violations.filter(
        (violation) => violation?.level === ViolationLevel.WARNING,
      ).length ?? 0)
    : 0;
  const customCheckInfoCount = isCustomCheck
    ? (task.result?.violations.filter(
        (violation) => violation?.level === ViolationLevel.INFO,
      ).length ?? 0)
    : 0;
  const lintErrorsCount = isLinter
    ? (task.result?.diagnostics.filter(
        (violation) => violation.level === LintDiagnosticLevel.ERROR,
      ).length ?? 0)
    : 0;

  const lintWarningsCount = isLinter
    ? (task.result?.diagnostics.filter(
        (violation) => violation.level === LintDiagnosticLevel.WARNING,
      ).length ?? 0)
    : 0;

  const failsUpstreamCount =
    task.__typename === 'DownstreamCheckTask'
      ? task.results?.filter((result) => result.failsUpstreamWorkflow).length ||
        0
      : 0;
  const location = useLocation();
  const internalGraphLinking = useInternalGraphLinking();

  return (
    <ConditionalLink
      to={getLinkToTask(task, task.workflow, location, internalGraphLinking)}
    >
      <div
        className={classnames(
          'group flex items-center justify-between rounded px-2 py-1 hover:bg-secondary',
          className,
        )}
      >
        <div className="flex items-center truncate">
          <TaskStatusIcon className="size-4" task={task} />
          <div
            className={classnames(
              'truncate pl-2 text-base text-secondary',
              (task.status === GraphQLTypes.CheckWorkflowTaskStatus.FAILED ||
                lintWarningsCount > 0) &&
                'font-semibold',
            )}
          >
            {isBuild ? (
              'Build'
            ) : isDownstream ? (
              <>
                <span>Downstream</span>
                {failsUpstreamCount !== 0 && (
                  <span className="ml-2 text-secondary">
                    {failsUpstreamCount} blocking
                  </span>
                )}
              </>
            ) : isLinter ? (
              'Linter'
            ) : isProposalCheck ? (
              'Proposals'
            ) : isCustomCheck ? (
              'Custom Check'
            ) : (
              <>
                <span>Operations</span>
                {crossedThreshold && (
                  <span className="ml-2 text-secondary">
                    Cardinality Exceeded
                  </span>
                )}
              </>
            )}
          </div>
          {task.__typename === 'CompositionCheckTask' &&
            task.result &&
            task.result.errors.length > 0 && (
              <p className="ml-2 text-base text-secondary">
                {task.result.errors.length}{' '}
                {format.pluralize(task.result.errors.length, 'Error')}
              </p>
            )}
          {task.__typename === 'OperationsCheckTask' &&
            task.result?.checkSeverity ===
              GraphQLTypes.ChangeSeverity.FAILURE &&
            !crossedThreshold && (
              <span className="ml-2 truncate text-base text-secondary">
                {task.result.numberOfAffectedOperations} Affected
              </span>
            )}
          {isCustomCheck && customCheckErrorsCount > 0 && (
            <p className="ml-2 truncate text-base text-secondary">
              {customCheckErrorsCount}{' '}
              {format.pluralize(customCheckErrorsCount, 'Error')}
            </p>
          )}
          {isCustomCheck && customCheckWarningsCount > 0 && (
            <p
              className={classnames(
                'truncate text-base text-secondary',
                customCheckErrorsCount === 0 ? 'ml-2' : 'ml-1',
              )}
            >
              {customCheckErrorsCount > 0 && '& '}
              {customCheckWarningsCount}{' '}
              {format.pluralize(customCheckWarningsCount, 'Warning')}
            </p>
          )}
          {isCustomCheck && customCheckInfoCount > 0 && (
            <p
              className={classnames(
                'text-base text-secondary',
                customCheckWarningsCount === 0 ? 'ml-2' : 'ml-1',
              )}
            >
              {customCheckWarningsCount > 0 && '& '}
              {customCheckInfoCount}{' '}
              {format.pluralize(customCheckInfoCount, 'Info')}
            </p>
          )}
          {isLinter && lintErrorsCount > 0 && (
            <p className="ml-2 text-base text-secondary">
              {lintErrorsCount} {format.pluralize(lintErrorsCount, 'Error')}
            </p>
          )}
          {isLinter && lintWarningsCount > 0 && (
            <p
              className={classnames(
                'text-base text-secondary',
                lintErrorsCount === 0 ? 'ml-2' : 'ml-1',
              )}
            >
              {lintErrorsCount > 0 && '& '}
              {lintWarningsCount}{' '}
              {format.pluralize(lintWarningsCount, 'Warning')}
            </p>
          )}
        </div>

        <IconArrowRight className="invisible text-placeholder group-hover:visible" />
      </div>
    </ConditionalLink>
  );
};
